import { ApolloError, useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { SafeDialog } from "components/Dialog";
import { currentTierIIReportingYear } from "encamp-shared/src/constants/tierii";
import { chemicalExclusionWarning } from "encamp-shared/src/tierii/chemicalExclusionWarning";
import { gql } from "generated-graphql";
import { HealthHazard, PhysicalHazard } from "generated-graphql/graphql";
import pluralize from "pluralize";
import { useMemo, useState } from "react";
import {
  HealthHazardLabels,
  HealthHazardOrder,
  PhysicalHazardLabels,
  PhysicalHazardOrder,
} from "util/hazards";

import { useAlerts } from "../../../../../components/Alerts/AlertProvider";
import { useFacilityUsage } from "./useFacilityUsage";

const SET_CHEMICAL_EXCLUDE_FROM_TIER_II_REPORTING = gql(`
  mutation SetChemicalExcludeFromTierIIReporting(
    $id: ID!
    $value: Boolean!
    $acknowledgementReason: String
  ) {
    setChemicalExcludeFromTierIIReporting(
      id: $id
      value: $value
      acknowledgementReason: $acknowledgementReason
    ) {
      id
      noHazardsNotReporting
    }
  }
`);

const CHEMICAL_REPORTING_FACILITY_SUMMARY = gql(`
  query ChemicalReportingFacilitySummary($chemicalId: ID!, $reportingYear: Int!) {
    chemicalReportingFacilitySummary(chemicalId: $chemicalId, reportingYear: $reportingYear) {
      overThresholdCount
      alwaysReportedCount
    }
  }
`);

type ExcludeFromTierIIDialogProps = {
  open: boolean;
  onClose: () => void;
  chemicalId: string;
  chemicalName: string;
  currentValue: boolean;
  physicalHazards: PhysicalHazard[];
  healthHazards: HealthHazard[];
  onSuccess?: (newValue: boolean) => void;
};

export function ExcludeFromTierIIDialog(props: ExcludeFromTierIIDialogProps) {
  const alerts = useAlerts();

  const targetValue = !props.currentValue;
  const flippingOn = targetValue;

  const showWarning = useMemo(
    () =>
      flippingOn &&
      chemicalExclusionWarning({
        noHazardsNotReporting: true,
        physicalHazards: props.physicalHazards,
        healthHazards: props.healthHazards,
      }) === "hazards_present",
    [flippingOn, props.physicalHazards, props.healthHazards]
  );

  const [acknowledged, setAcknowledged] = useState(false);
  const [reason, setReason] = useState("");

  const { data: facilityUsageData, loading: facilityUsageLoading } = useFacilityUsage({
    chemicalId: props.chemicalId,
    includeItems: true,
    page: 0,
    pageSize: 25,
    skip: !props.open,
  });
  const facilityCount = facilityUsageData?.facilityUsage.facilityCount ?? 0;
  const tenantCount = facilityUsageData?.facilityUsage.tenantCount ?? 0;
  const thisYearFacilityCount = facilityUsageData?.facilityUsage.thisYearFacilityCount ?? 0;
  const facilityItems = facilityUsageData?.facilityUsage.items ?? [];

  const { data: rfcSummary, loading: rfcSummaryLoading } = useQuery(
    CHEMICAL_REPORTING_FACILITY_SUMMARY,
    {
      variables: {
        chemicalId: props.chemicalId,
        reportingYear: currentTierIIReportingYear,
      },
      skip: !flippingOn || !props.open,
      fetchPolicy: "cache-and-network",
    }
  );
  const overThresholdCount = rfcSummary?.chemicalReportingFacilitySummary.overThresholdCount ?? 0;
  const alwaysReportedCount = rfcSummary?.chemicalReportingFacilitySummary.alwaysReportedCount ?? 0;

  const [setExclude, { loading: saving }] = useMutation(
    SET_CHEMICAL_EXCLUDE_FROM_TIER_II_REPORTING,
    {
      refetchQueries: ["ChemicalDetailChemicalQuery", "TenantCatalogChemicals"],
    }
  );

  const submitDisabled = showWarning && (!acknowledged || reason.trim().length === 0);

  const handleSubmit = async () => {
    try {
      await setExclude({
        variables: {
          id: props.chemicalId,
          value: targetValue,
          acknowledgementReason: showWarning ? reason.trim() : null,
        },
      });
      alerts.success(
        targetValue
          ? "Chemical marked as excluded from Tier II reporting"
          : "Chemical no longer excluded from Tier II reporting"
      );
      props.onSuccess?.(targetValue);
      props.onClose();
    } catch (err) {
      console.error(err);
      alerts.error(err instanceof ApolloError ? err.message : "Failed to update Tier II exclusion");
    }
  };

  const physicalHazardLabels = PhysicalHazardOrder.filter((h) =>
    props.physicalHazards.includes(h)
  ).map((h) => PhysicalHazardLabels[h]);
  const healthHazardLabels = HealthHazardOrder.filter((h) => props.healthHazards.includes(h)).map(
    (h) => HealthHazardLabels[h]
  );

  return (
    <SafeDialog open={props.open} onClose={props.onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {flippingOn
          ? `Exclude ${props.chemicalName} from Tier II reporting?`
          : `Resume Tier II reporting for ${props.chemicalName}?`}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {flippingOn && (
            <Typography variant="body2" color="text.secondary">
              This setting lives on the chemical catalog entry and applies to{" "}
              <strong>every facility that uses this chemical</strong>.
            </Typography>
          )}

          {showWarning && (
            <Alert severity="warning">
              This chemical has hazards listed. Excluding it from Tier II reporting may cause
              compliance gaps. Confirm this is intentional below.
            </Alert>
          )}

          {flippingOn && (physicalHazardLabels.length > 0 || healthHazardLabels.length > 0) && (
            <Stack spacing={0.5}>
              <Typography variant="body2" fontWeight={600}>
                Hazards on chemical
              </Typography>
              {physicalHazardLabels.length > 0 && (
                <Typography variant="body2" color="text.secondary">
                  Physical: {physicalHazardLabels.join(", ")}
                </Typography>
              )}
              {healthHazardLabels.length > 0 && (
                <Typography variant="body2" color="text.secondary">
                  Health: {healthHazardLabels.join(", ")}
                </Typography>
              )}
            </Stack>
          )}

          {(facilityUsageLoading || facilityCount > 0) && (
            <Stack spacing={0.5}>
              <Typography variant="body2" fontWeight={600}>
                Affected scope
              </Typography>
              {facilityUsageLoading ? (
                <>
                  <Skeleton variant="text" width="80%" />
                  {flippingOn && <Skeleton variant="text" width="55%" />}
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" width="50%" />
                  <Skeleton variant="text" width="65%" />
                </>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary">
                    {facilityCount} {pluralize("facility", facilityCount)} across {tenantCount}{" "}
                    {pluralize("tenant", tenantCount)}
                    {thisYearFacilityCount > 0
                      ? ` (${thisYearFacilityCount} ${pluralize("facility", thisYearFacilityCount)} in RY ${currentTierIIReportingYear})`
                      : ""}
                    .
                  </Typography>
                  {flippingOn && rfcSummaryLoading ? (
                    <Skeleton variant="text" width="55%" />
                  ) : (
                    flippingOn &&
                    (overThresholdCount > 0 || alwaysReportedCount > 0) && (
                      <Typography variant="body2" color="warning.main">
                        RY {currentTierIIReportingYear}:{" "}
                        {overThresholdCount > 0 &&
                          `${overThresholdCount} ${pluralize("facility", overThresholdCount)} over threshold`}
                        {overThresholdCount > 0 && alwaysReportedCount > 0 ? ", " : ""}
                        {alwaysReportedCount > 0 &&
                          `${alwaysReportedCount} ${pluralize("facility", alwaysReportedCount)} marked Always Reported`}
                        .
                      </Typography>
                    )
                  )}
                  {facilityItems.length > 0 && (
                    <Box sx={{ maxHeight: 160, overflow: "auto" }}>
                      {facilityItems.map((f) => (
                        <Typography
                          key={f.id}
                          variant="body2"
                          color="text.secondary"
                          component="div"
                        >
                          {f.name} — {f.tenant?.name}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </>
              )}
            </Stack>
          )}

          {showWarning && (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={acknowledged}
                    onChange={(_, checked) => setAcknowledged(checked)}
                    data-testid="exclude-from-tier-ii-acknowledge"
                  />
                }
                label="I confirm this chemical should not be reported on Tier II despite having hazards listed."
              />
              <TextField
                label="Reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                multiline
                minRows={2}
                fullWidth
                required
                helperText="Required when hazards are present."
              />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitDisabled}
          loading={saving}
          data-testid="exclude-from-tier-ii-submit"
        >
          {flippingOn ? "Exclude" : "Resume reporting"}
        </Button>
      </DialogActions>
    </SafeDialog>
  );
}
